import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingDashboardDetails, BenchmarkingProviderFlyoutAddressTable, BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingFiltersContext, BenchmarkingServiceAreaFilters } from "@app/overviews/network-benchmarking-dashboard/filters";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { getClaimsVolumeDefinitions, getEfficiencyAndEffectivenessDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NPF } from "@app/projects/servicing-provider/types";
import { Accordion, AccordionContainer, InfoIndicator } from "@app/shared";
import { valueOrDashes } from "@app/shared/utility";

interface BenchmarkingProviderFlyoutProps {
  dashboardDetails: BenchmarkingDashboardDetails;
  formatNetworkList: (networkList: string[]) => JSX.Element;
}

const BenchmarkingProviderFlyout = (props: BenchmarkingProviderFlyoutProps): JSX.Element => {
  const [benchmarkingProviderFlyoutData, setBenchmarkingProviderFlyoutData] = useState<BenchmarkingProviderFlyoutData>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);

  useEffect(() => {
    if (!!props.dashboardDetails?.npi) {
      getProviderFlyoutData();
    }
  }, [props.dashboardDetails]);

  const getProviderFlyoutData = (): void => {
    setLoading(true);

    const payload: NetworkComparisonFiltersPayload = benchmarkingContext.getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters));
    payload.npi = props.dashboardDetails.npi;

    benchmarkingFiltersContext.getService().getProviderFlyoutData(payload).then((data: BenchmarkingProviderFlyoutData) => {
      if (!!data) {
        setBenchmarkingProviderFlyoutData(data);
        setLoading(false);
      }
    });
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-md-12">
          <div>
            <h1 className="pl0-imp">
              {props.dashboardDetails.providerName}
            </h1>
            <div>
              NPI: {props.dashboardDetails?.npi}
            </div>
          </div>
        </div>
      </div>
      <BenchmarkingServiceAreaFilters />

      <AccordionContainer className="mt-3">
        <Accordion
          defaultOpen={true}
          testId={`benchmarking-dashboard--provider-flyout--provider-details-${props.dashboardDetails?.npi}`}
          title="Provider Details"
        >
          {!!props.dashboardDetails && (
            <React.Fragment>
              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">Specialty</label> {
                  (benchmarkingProviderFlyoutData?.specialties || [null]).map((specialty, index) => { return (
                    <div className="mb-3" key={index}>
                      {valueOrDashes(specialty)}
                    </div>)})}
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">Effectiveness (Quality) <InfoIndicator className={"informational-indicator"} tooltipTitle={getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank)} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.effectivenessRank)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">Efficiency (Cost) <InfoIndicator className={"informational-indicator"} tooltipTitle={getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank)} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.efficiencyRank)}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">PCA - Medicare <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.medicareClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">PCA - Medicaid <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.medicaidClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">PCA - Commercial <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.commercialClaimsLabel)}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">PCA - Total <InfoIndicator className={"informational-indicator"} tooltipTitle={getClaimsVolumeDefinitions()} /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.totalClaimsLabel)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">Network Count <InfoIndicator className={"informational-indicator"} tooltipTitle="Count of selected networks a provider is contracted with" /></label>
                  <div className="mb-3">
                    {valueOrDashes(props.dashboardDetails.networkCount)}
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-4">
                  <label className="font-weight-bold mb-0">Network List <InfoIndicator className={"informational-indicator"} tooltipTitle="List of all selected networks a provider is contracted with" /></label>
                  <div className="mb-3">
                    {props.formatNetworkList(props.dashboardDetails.networkList)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </Accordion>

        <Accordion
          testId={`benchmarking-dashboard--provider-flyout--address-${props.dashboardDetails?.npi}`}
          title="Address"
        >
          <BenchmarkingProviderFlyoutAddressTable
            benchmarkingProviderFlyoutData={benchmarkingProviderFlyoutData}
            loading={loading}
            npi={props.dashboardDetails.npi}
            formatNetworkList={props.formatNetworkList}
          />
        </Accordion>
      </AccordionContainer>
    </React.Fragment>
  );
}

export default BenchmarkingProviderFlyout;
